import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useHistory, useParams } from "react-router-dom";
import dayjs from "dayjs";

import { Box, Button, CircularProgress, FormControl, IconButton, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Chip, Stack } from "@mui/material";
import { DeleteOutlined, UploadFile, AttachFile } from '@mui/icons-material'

import { Container, Cell, ButtonContainer } from "./styled";
import { IPerson } from "./types";

import UploadArea from "components/upload-area";
import { useAlert } from "contexts/alert";
import { cpfMask } from "utils/cpf";
import api from "services/api";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc)

function formatDate(dateToFormat: string | null) {
  try {
    if (!dateToFormat) throw new Error();
    if (!dayjs(dateToFormat).isValid()) throw new Error();
    
    return dayjs(dateToFormat).utc().format(`DD/MM/YYYY`);
  } catch (error) {
    return `-`
  }
}

export function AdesaoView() {
  const { workspaceId, templateId, workspaceActivityId } = useParams() as { workspaceId: string, templateId: string, workspaceActivityId: string };
  const history = useHistory();
  const { openAlert } = useAlert();
  
  const [people, setPeople] = useState<IPerson[] | null>(null);
  const [files, setFiles] = useState<Record<string, File[]>>({});
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { data: originalData, isFetching: isQueryFetching, isFetched: isQueryFetched, isError: isQueryError } = useQuery({
    queryKey: ["adesao", workspaceActivityId],
    queryFn: () => api.get<{ data: IPerson[] }>(`/v2/workspaces/${workspaceId}/templates/${templateId}/adesao/${workspaceActivityId}`),
    select: (data) => data.data.data,
  });

  const { isSuccess: isMutateSuccess, isPending: isMutatePending, isError: isMutateError, mutate } = useMutation({
    mutationFn: async (payload: IPerson[]) => {
      const modifiedData = payload.filter(person => person.wasModified === true);
      
      await api.post(`/v2/workspaces/${workspaceId}/templates/${templateId}/adesao/${workspaceActivityId}`, modifiedData);

      const uploads = Object.entries(files).filter(([_, fileArray]) => fileArray && fileArray.length > 0);

      if (uploads.length > 0) {
        const uploadPromises = uploads.flatMap(([cpf, fileArray]) =>
          fileArray.map(file => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('cpf', cpf);

            return api.post(`/v2/workspaces/${workspaceId}/templates/${templateId}/adesao/${workspaceActivityId}/upload-files`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            }).then(() => {
              console.log(`Arquivo ${file.name} de ${cpf} enviado com sucesso.`);
            }).catch(err => {
              console.error(`Erro ao enviar arquivo ${file.name} de ${cpf}:`, err);
              throw err;
            });
          })
        );

        await Promise.all(uploadPromises);
      }
    },
    onSuccess: () => {
      openAlert('Dados salvos com sucesso', 'success');
      history.push(`/v2/workspaces/${workspaceId}/templates/${templateId}/movimentacao`);
    },
    onError: (err) => {
      console.log("[Error @ Adesao]", err)
      openAlert('Algo deu errado ao salvar dados', 'error');
    },
  });

  useEffect(() => {
    if (originalData && isQueryFetched) {
      setPeople(originalData);
    };
  }, [originalData]);

  function handleChangePlan(personCpf: string, planId: string) {
    if (!people) return;

    const wasModified = !(originalData?.find(originalPerson => originalPerson.taxPayerId === personCpf && originalPerson.chosenPlan === planId)) ? true : false
    
    const copyChanged = [...people].map(person => person.taxPayerId !== personCpf ? person : ({
      ...person,
      wasModified,
      chosenPlan: planId,
    }));

    setPeople((prev) => [...copyChanged]);
  };

  function handleChangeDate(personCpf: string, newDate: string) {
    if (!people) return;

    // A pessoa precisa entrar no lote enviado mesmo quando o único campo
    // tocado é a data (D18): o envio filtra por `wasModified === true`,
    // e só `handleChangePlan` marcava isso antes deste fix.
    const copyChanged = [...people].map(person => person.taxPayerId !== personCpf ? person : ({
      ...person,
      wasModified: true,
      selectedDate: newDate,
    }));

    setPeople((prev) => [...copyChanged]);
  };

  function handleCancelPlan() {
    if (originalData) setPeople(originalData);
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>, taxPayerId: string) {
    const selectedFiles = event?.target?.files;
    
    if (!selectedFiles || selectedFiles.length === 0) {
      openAlert('Por favor, selecione pelo menos um arquivo válido.', 'error');
      return;
    }

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    Array.from(selectedFiles).forEach(file => {
      const isPDF = file.type === "application/pdf";
      const isJPEG = file.type === "image/jpeg";
      const isPNG = file.type === "image/png";
      
      if (isPDF || isJPEG || isPNG) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      openAlert(`Arquivos inválidos (somente PDF, JPEG e PNG são aceitos): ${invalidFiles.join(', ')}`, 'error');
    }

    if (validFiles.length > 0) {
      setFiles(prev => ({ 
        ...prev, 
        [taxPayerId]: [...(prev[taxPayerId] || []), ...validFiles] 
      }));
    }

    // Reset the input value to allow selecting the same files again if needed
    event.target.value = '';
  };

  function handleRemoveFile (event: React.MouseEvent, taxPayerId: string, fileIndex?: number) {
    event.stopPropagation();
    
    setFiles(prev => {
      const currentFiles = prev[taxPayerId] || [];
      
      if (fileIndex !== undefined) {
        // Remove specific file
        const newFiles = currentFiles.filter((_, index) => index !== fileIndex);
        return { ...prev, [taxPayerId]: newFiles };
      } else {
        // Remove all files
        return { ...prev, [taxPayerId]: [] };
      }
    });
  };

  function handleRemoveAllFiles (event: React.MouseEvent, taxPayerId: string) {
    event.stopPropagation();
    setFiles(prev => ({ ...prev, [taxPayerId]: [] }));
  };

  return (
    <>
      {!isQueryFetched && isQueryFetching && <div>Loading...</div>}
      {!isQueryFetched && isQueryError && <div>Error...</div>}
      {people && (
       <Container>
          <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
            <Table>
              <TableHead>
                <TableRow style={{ height: '100px'}}>
                  <TableCell>CPF do beneficiário</TableCell>
                  <TableCell>CPF do titular vinculado</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Data de nascimento</TableCell>
                  <TableCell>Data de admissão</TableCell>
                  <TableCell>Plano</TableCell>
                  <TableCell>Upload arquivos</TableCell>
                  <TableCell>Data de inicio do plano</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {people.map(person => (
                  <TableRow key={person.taxPayerId} style={{ minHeight: '100px' }}>
                    <Cell>{cpfMask(person.taxPayerId)}</Cell>
                    <Cell>{person.linkedTaxPayerId ? cpfMask(person.linkedTaxPayerId) : '-'}</Cell>
                    <Cell>{person.name}</Cell>
                    <Cell>{person.type === 'T' ? 'Titular' : 'Dependente'}</Cell>
                    <Cell>{formatDate(person.birthDate)}</Cell>
                    <Cell>{formatDate(person.hiringDate)}</Cell>
                    <Cell>
                      <FormControl style={{ width: '250px' }}>
                        <Select
                          variant="standard"
                          value={person.chosenPlan ?? ""}
                          onChange={e => handleChangePlan(person.taxPayerId, e.target.value)}
                          disabled={isMutateSuccess}
                          style={{ padding: '10px 0px 10px 0px' }}
                        >
                          <MenuItem value='NONE'>Sem Plano</MenuItem>
                          {person.plans.map(plan => (
                            <MenuItem key={`${plan.id}${person.taxPayerId}`} value={plan.name}>{plan.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Cell>
                    <Cell style={{ height: 'inherit', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-start', padding: '10px' }}>
                      <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ flex: 1 }}>
                          <input
                            accept="application/pdf,image/jpeg,image/png"
                            style={{ display: 'none' }}
                            id={`${person.taxPayerId}-upload`}
                            type="file"
                            multiple
                            onChange={(e) => handleFileChange(e, person.taxPayerId)}
                          />
                          <label htmlFor={`${person.taxPayerId}-upload`}>
                            <Button 
                              variant="outlined"
                              component="span"
                              sx={{
                                width: '100%',
                                height: '50px',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                paddingRight: 1,
                              }}
                            >
                              <UploadFile sx={{ marginRight: 1 }} fontSize="small" color="primary" />
                              <Box component="span" sx={{ flexGrow: 1 }}>
                                {files[person.taxPayerId]?.length > 0 
                                  ? `Adicionar mais arquivos (${files[person.taxPayerId].length} selecionados)`
                                  : 'Selecionar arquivos (PDF, JPEG, PNG)'}
                              </Box>
                            </Button>
                          </label>
                        </Box>
                        {files[person.taxPayerId]?.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleRemoveAllFiles(e, person.taxPayerId)}
                            sx={{ color: 'black' }}
                            title="Remover todos os arquivos"
                          >
                            <DeleteOutlined fontSize="small" color="error" />
                          </IconButton>
                        )}
                      </Box>
                      
                      {/* Display selected files */}
                      {files[person.taxPayerId]?.length > 0 && (
                        <Stack direction="column" spacing={1} sx={{ width: '100%', maxHeight: '120px', overflow: 'auto' }}>
                          {files[person.taxPayerId].map((file, index) => (
                            <Chip
                              key={`${person.taxPayerId}-${index}-${file.name}`}
                              icon={<AttachFile fontSize="small" />}
                              label={file.name}
                              variant="outlined"
                              size="small"
                              onDelete={(e) => handleRemoveFile(e, person.taxPayerId, index)}
                              deleteIcon={<DeleteOutlined fontSize="small" />}
                              sx={{
                                justifyContent: 'space-between',
                                maxWidth: '250px',
                                '& .MuiChip-label': {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '180px'
                                }
                              }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Cell>
                    <Cell>
                      <TextField
                        type='date'
                        variant='filled'
                        margin='normal'
                        disabled={!person.chosenPlan || person.chosenPlan === 'NONE' || isMutateSuccess}
                        value={person.selectedDate}
                        onChange={(e) => handleChangeDate(person.taxPayerId, e.target.value)}
                      />
                    </Cell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <ButtonContainer>
              <Button
                variant='text'
                color="error"
                style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                disabled={isMutatePending || isMutateSuccess || !(people.find(person => person.wasModified === true))}
                onClick={handleCancelPlan}
              >
                Limpar
              </Button>
              <Button
                variant='contained'
                style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                disabled={isMutatePending || isMutateSuccess || !(people.find(person => person.wasModified === true))}
                onClick={() => mutate(people)}
              >
                {isMutatePending ? <CircularProgress color='info' size={20}/> : 'Salvar'}
              </Button>
          </ButtonContainer>
        </Container>
      )}
    </>
  );
}
